/**
 * Lernziel-Register (PRD mastery-objectives M2/M3) — pure helpers for
 * `app-objective-register`, tested in objective-register.spec.ts.
 */
import { ObjectiveRegister, RegisterLink, RegisterObjective } from '../../services/course-structure.service';

export const BLOOM = ['remember', 'understand', 'apply', 'analyze', 'evaluate', 'create'] as const;
export const DQR = ['knowledge', 'skills', 'social', 'autonomy'] as const;

export interface GoalLinkView {
  key: string;
  text: string;
  status: RegisterLink['status'];
  origin: RegisterLink['origin'];
  score: string;
}

export interface GoalView {
  key: string;
  text: string;
  /** curriculum | import | own */
  origin: 'curriculum' | 'import' | 'own';
  active: boolean;
  confirmed: GoalLinkView[];
  proposed: GoalLinkView[];
  /** fine objectives not yet linked (for "add link") */
  candidates: { key: string; text: string }[];
}

export interface ObjectiveView {
  key: string;
  text: string;
  source: 'material' | 'lecturer';
  chapter: string;
  bloom: string | null;
  dqr: string | null;
  active: boolean;
}

export interface RegisterView {
  goals: GoalView[];
  objectives: ObjectiveView[];
  proposedTotal: number;
}

function goalOrigin(o: RegisterObjective): GoalView['origin'] {
  if (o.external_ref?.startsWith('curriculum:')) return 'curriculum';
  return o.external_ref ? 'import' : 'own';
}

/** The register as the dialog shows it: client goals with their links, then
 *  the fine objectives (material + lecturer) in chapter order. */
export function buildRegisterView(reg: ObjectiveRegister | null): RegisterView {
  if (!reg) return { goals: [], objectives: [], proposedTotal: 0 };
  const fine = reg.objectives.filter((o) => o.source !== 'client');
  const text = new Map(fine.map((o) => [o.objective_key, o.description]));
  const activeFine = fine.filter((o) => o.status === 'active');
  let proposedTotal = 0;
  const goals = reg.objectives.filter((o) => o.source === 'client').map((g) => {
    const links = (reg.links[g.objective_key] || []).filter((l) => text.has(l.source_key));
    const view = (l: RegisterLink): GoalLinkView => ({
      key: l.source_key, text: text.get(l.source_key) || l.source_key, status: l.status, origin: l.origin,
      score: l.score == null ? '' : `${Math.round(l.score * 100)} %`,
    });
    const confirmed = links.filter((l) => l.status === 'confirmed').map(view);
    const proposed = links.filter((l) => l.status === 'proposed').map(view);
    proposedTotal += proposed.length;
    const taken = new Set(links.filter((l) => l.status !== 'rejected').map((l) => l.source_key));
    return {
      key: g.objective_key, text: g.description, origin: goalOrigin(g), active: g.status === 'active',
      confirmed, proposed,
      candidates: activeFine.filter((o) => !taken.has(o.objective_key)).map((o) => ({ key: o.objective_key, text: o.description })),
    };
  });
  const objectives = fine.map((o) => ({
    key: o.objective_key, text: o.description, source: o.source as 'material' | 'lecturer',
    chapter: o.chapter_title || '', bloom: o.bloom_level, dqr: o.dqr_area, active: o.status === 'active',
  }));
  return { goals, objectives, proposedTotal };
}

export interface GoalImportRow {
  description: string;
  bloom_level: string | null;
  external_ref: string | null;
}

const HEADER = /^(beschreibung|description|lernziel|ziel|goal|objective)$/i;
const BLOOM_ALIASES: Record<string, string> = {
  erinnern: 'remember', verstehen: 'understand', anwenden: 'apply', analysieren: 'analyze',
  bewerten: 'evaluate', erschaffen: 'create', erstellen: 'create',
};

/** A goal table pasted or exported as CSV (`;`, `,` or tab): column 1 the
 *  goal, optional column 2 the Bloom level (English or German), optional
 *  column 3 the institution's own reference. A header row is skipped. */
export function parseGoalsCsv(raw: string): GoalImportRow[] {
  const lines = String(raw || '').replace(/^﻿/, '').split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (!lines.length) return [];
  const sep = [';', '\t', ','].find((s) => lines[0].includes(s)) || ';';
  const rows = lines.map((l) => splitCsv(l, sep));
  if (rows[0][0] && HEADER.test(rows[0][0].trim())) rows.shift();
  return rows
    .map((cells) => {
      const description = (cells[0] || '').trim();
      const b = (cells[1] || '').trim().toLowerCase();
      const bloom = (BLOOM as readonly string[]).includes(b) ? b : BLOOM_ALIASES[b] || null;
      return { description, bloom_level: bloom, external_ref: (cells[2] || '').trim() || null };
    })
    .filter((r) => r.description)
    .slice(0, 500);
}

function splitCsv(line: string, sep: string): string[] {
  const out: string[] = [];
  let cur = '';
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (quoted && line[i + 1] === '"') {
        cur += '"';
        i++;
      } else quoted = !quoted;
    } else if (ch === sep && !quoted) {
      out.push(cur);
      cur = '';
    } else cur += ch;
  }
  out.push(cur);
  return out;
}
